import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { LIVE_DRAFT_YEAR } from "../../config/api";
import { publicApi } from "../../api/http";
import { nhlTeamColors } from "../../components/PlayerCard/Constants";
import PageLoader from "../../components/PageLoader/PageLoader";
import "./LiveDraft.css";

const POLL_MS = 5000;

function hexToRgba(hex, alpha) {
  if (!hex) return null;
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function teamColor(teamId) {
  return teamId ? nhlTeamColors[teamId] : null;
}

function teamClockStyle(teamId) {
  const hex = teamColor(teamId);
  if (!hex) return undefined;
  return {
    borderTop: `4px solid ${hex}`,
    background: `linear-gradient(180deg, ${hexToRgba(hex, 0.1)} 0%, var(--bg-surface) 45%)`,
  };
}

function teamAccentBorder(teamId) {
  const hex = teamColor(teamId);
  if (!hex) return undefined;
  return { borderLeftColor: hex };
}

function pickKey(pick) {
  return `${pick.round}-${pick.pick}`;
}

function PickRevealOverlay({ pick, onDismiss }) {
  const player = pick?.player;
  const team = pick?.team;
  const teamId = pick?.team_id || team?.team_id;
  const hex = teamColor(teamId);
  const cardStyle = hex
    ? {
        borderColor: hex,
        boxShadow: `0 0 60px ${hexToRgba(hex, 0.35)}, 0 24px 48px rgba(0, 0, 0, 0.45)`,
        background: `linear-gradient(165deg, ${hexToRgba(hex, 0.22)} 0%, var(--bg-surface) 55%)`,
      }
    : undefined;

  if (!pick || !player) return null;

  const playerName = `${player.first_name} ${player.last_name}`;

  return (
    <div className="live-draft-pick-reveal" role="dialog" aria-label={`${playerName} drafted`}>
      <button
        type="button"
        className="live-draft-pick-reveal-backdrop"
        aria-label="Dismiss pick announcement"
        onClick={onDismiss}
      />
      <div className="live-draft-pick-reveal-card" style={cardStyle}>
        <div className="live-draft-pick-reveal-shine" aria-hidden="true" />
        <div className="live-draft-pick-reveal-sparks" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
        <p className="live-draft-pick-reveal-eyebrow">Pick is in</p>
        <div className="live-draft-pick-reveal-badge">Drafted</div>
        <p className="live-draft-pick-reveal-pick">
          #{pick.overall_pick}
          <span className="live-draft-pick-reveal-pick-meta">
            Round {pick.round} · Pick {pick.pick}
          </span>
        </p>
        <div className="live-draft-pick-reveal-player">
          {player.player_image_url ? (
            <img
              src={player.player_image_url}
              alt=""
              className="live-draft-pick-reveal-photo"
            />
          ) : (
            <div className="live-draft-pick-reveal-photo live-draft-pick-reveal-photo--placeholder" />
          )}
          <div className="live-draft-pick-reveal-copy">
            <h2 className="live-draft-pick-reveal-name">{playerName}</h2>
            {player.position && (
              <p className="live-draft-pick-reveal-position">{player.position}</p>
            )}
          </div>
        </div>
        <div className="live-draft-pick-reveal-team">
          {team?.team_logo_url && (
            <img src={team.team_logo_url} alt="" className="live-draft-team-logo" />
          )}
          <span>{team?.team_name || "—"}</span>
        </div>
      </div>
    </div>
  );
}

function formatOdds(value) {
  if (value == null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function RemainingBoard({ players }) {
  return (
    <section className="live-draft-panel live-draft-col-panel">
      <h2 className="live-draft-panel-title">On the board</h2>
      {players?.length ? (
        <ul className="live-draft-board-list">
          {players.map((player) => (
            <li key={player.player_id} className="live-draft-board-item">
              <Link to={`/player/${player.player_id}`} className="live-draft-board-link">
                {player.player_image_url && (
                  <img src={player.player_image_url} alt="" className="live-draft-player-thumb" />
                )}
                <span className="live-draft-board-copy">
                  <span className="live-draft-player-name">
                    {player.first_name} {player.last_name}
                  </span>
                  <span className="live-draft-available-meta">
                    {player.position && <span>{player.position}</span>}
                    {player.consensus_ranking != null && (
                      <span>Cons. #{player.consensus_ranking}</span>
                    )}
                  </span>
                </span>
                <span className="live-draft-odds">{formatOdds(player.odds)}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="live-draft-muted">No odds available for the current pick.</p>
      )}
    </section>
  );
}

function ClockCenter({ slot, upcoming }) {
  const clockStyle = teamClockStyle(slot?.team_id);

  return (
    <div className="live-draft-center">
      <section
        className={`live-draft-panel live-draft-panel--clock live-draft-col-panel${clockStyle ? " live-draft-panel--team" : ""}`}
        style={clockStyle}
      >
        <p className="live-draft-eyebrow">On the clock</p>
        {!slot ? (
          <>
            <h2 className="live-draft-panel-title">Waiting for next pick</h2>
            <p className="live-draft-muted">Draft complete or between picks.</p>
          </>
        ) : (
          <div className="live-draft-clock-hero">
            {slot.team_logo && (
              <img src={slot.team_logo} alt="" className="live-draft-team-logo live-draft-team-logo--xl" />
            )}
            <h2 className="live-draft-clock-team">{slot.team_name}</h2>
            {slot.pick_from_team_name && (
              <p className="live-draft-clock-via">via {slot.pick_from_team_name}</p>
            )}
            <p className="live-draft-clock-pick">
              Round {slot.round} · Pick {slot.pick}
            </p>
            <p className="live-draft-clock-overall">Overall #{slot.overall_pick}</p>
          </div>
        )}
      </section>

      <section className="live-draft-panel live-draft-col-panel live-draft-up-next-panel">
        <h2 className="live-draft-panel-title">Up next</h2>
        {upcoming?.length ? (
          <ul className="live-draft-up-next-list">
            {upcoming.map((s) => (
              <li
                key={`${s.round}-${s.pick}`}
                className="live-draft-up-next-row live-draft-team-accent-row"
                style={teamAccentBorder(s.team_id)}
              >
                <span className="live-draft-up-next-num">#{s.overall_pick}</span>
                {s.team_logo && (
                  <img src={s.team_logo} alt="" className="live-draft-team-logo" />
                )}
                <span className="live-draft-up-next-team">
                  <span className="live-draft-up-next-team-name">{s.team_name}</span>
                  {s.pick_from_team_name && (
                    <span className="live-draft-up-next-via">via {s.pick_from_team_name}</span>
                  )}
                </span>
                <span className="live-draft-up-next-meta">
                  R{s.round} · P{s.pick}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="live-draft-muted">No upcoming picks on the board.</p>
        )}
      </section>
    </div>
  );
}

function FeedPickCard({ pick, justPicked }) {
  const player = pick.player;
  const team = pick.team;
  const teamId = pick.team_id || team?.team_id;
  const accentStyle = teamAccentBorder(teamId);

  if (pick.forfeited) {
    return (
      <article
        className={`live-draft-feed-card live-draft-feed-card--forfeited${justPicked ? " live-draft-feed-card--just-picked" : ""}`}
        style={accentStyle}
      >
        <div className="live-draft-feed-link live-draft-feed-link--forfeited">
          <span className="live-draft-feed-overall">#{pick.overall_pick}</span>
          <span className="live-draft-feed-copy">
            <span className="live-draft-feed-name">Pick forfeited</span>
            <span className="live-draft-feed-detail">
              <span>R{pick.round} · P{pick.pick}</span>
              <span>{pick.team_name || team?.team_name || "—"}</span>
            </span>
          </span>
          {team?.team_logo_url && (
            <img src={team.team_logo_url} alt="" className="live-draft-feed-team-logo" />
          )}
        </div>
      </article>
    );
  }

  return (
    <article
      className={`live-draft-feed-card${justPicked ? " live-draft-feed-card--just-picked" : ""}`}
      style={accentStyle}
    >
      {player ? (
        <Link to={`/player/${player.player_id}`} className="live-draft-feed-link">
          <span className="live-draft-feed-overall">#{pick.overall_pick}</span>
          {player.player_image_url ? (
            <img src={player.player_image_url} alt="" className="live-draft-feed-thumb" />
          ) : (
            <span className="live-draft-feed-thumb live-draft-feed-thumb--empty" />
          )}
          <span className="live-draft-feed-copy">
            <span className="live-draft-feed-name">
              {player.first_name} {player.last_name}
            </span>
            <span className="live-draft-feed-detail">
              <span>R{pick.round} · P{pick.pick}</span>
              {player.position && <span>{player.position}</span>}
              {player.consensus_ranking != null && (
                <span>Cons. #{player.consensus_ranking}</span>
              )}
            </span>
          </span>
          {team?.team_logo_url && (
            <img src={team.team_logo_url} alt="" className="live-draft-feed-team-logo" />
          )}
        </Link>
      ) : (
        <div className="live-draft-feed-link live-draft-feed-link--pending">
          <span className="live-draft-feed-overall">#{pick.overall_pick}</span>
          <span className="live-draft-feed-copy">
            <span className="live-draft-feed-name">Player pending</span>
            <span className="live-draft-feed-detail">
              R{pick.round} · P{pick.pick} · {team?.team_name || "—"}
            </span>
          </span>
        </div>
      )}
    </article>
  );
}

function PicksFeed({ picks, sortMode, onSortModeChange, highlightPickKey }) {
  const sortedPicks = useMemo(() => {
    const rows = [...(picks || [])];
    if (sortMode === "recent") {
      return rows.reverse();
    }
    return rows;
  }, [picks, sortMode]);

  return (
    <section className="live-draft-panel live-draft-col-panel live-draft-feed-panel">
      <div className="live-draft-feed-toolbar">
        <h2 className="live-draft-panel-title">Draft feed</h2>
        <div className="live-draft-feed-toggle" role="group" aria-label="Sort draft feed">
          <button
            type="button"
            className={`live-draft-feed-toggle-btn${sortMode === "recent" ? " is-active" : ""}`}
            onClick={() => onSortModeChange("recent")}
          >
            Most recent
          </button>
          <button
            type="button"
            className={`live-draft-feed-toggle-btn${sortMode === "order" ? " is-active" : ""}`}
            onClick={() => onSortModeChange("order")}
          >
            Pick order
          </button>
        </div>
      </div>
      {sortedPicks.length ? (
        <div className="live-draft-feed-stream">
          {sortedPicks.map((pick) => (
            <FeedPickCard
              key={`${pick.round}-${pick.pick}`}
              pick={pick}
              justPicked={highlightPickKey === pickKey(pick)}
            />
          ))}
        </div>
      ) : (
        <p className="live-draft-muted">No picks yet. The feed will update as selections are made.</p>
      )}
    </section>
  );
}

function LiveDraft() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feedSort, setFeedSort] = useState("recent");
  const revisionRef = useRef(null);
  const knownPicksRef = useRef(null);
  const [celebrationPick, setCelebrationPick] = useState(null);
  const [highlightPickKey, setHighlightPickKey] = useState(null);

  const registerPicks = (row) => {
    const picks = row?.previous_picks || [];
    const keys = picks.map(pickKey);

    if (knownPicksRef.current === null) {
      knownPicksRef.current = new Set(keys);
      return;
    }

    const newest = [...picks]
      .reverse()
      .find((pick) => !knownPicksRef.current.has(pickKey(pick)) && pick.player);

    knownPicksRef.current = new Set(keys);

    if (newest) {
      const key = pickKey(newest);
      setCelebrationPick(newest);
      setHighlightPickKey(key);
    }
  };

  useEffect(() => {
    if (!celebrationPick) return undefined;
    const dismissId = window.setTimeout(() => setCelebrationPick(null), 5200);
    return () => window.clearTimeout(dismissId);
  }, [celebrationPick]);

  useEffect(() => {
    if (!highlightPickKey) return undefined;
    const clearId = window.setTimeout(() => setHighlightPickKey(null), 8000);
    return () => window.clearTimeout(clearId);
  }, [highlightPickKey]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const params = revisionRef.current ? { revision: revisionRef.current } : undefined;
        const { data } = await publicApi.get("/live_draft/active", { params });
        if (cancelled) return;
        const row = typeof data === "string" ? JSON.parse(data) : data;
        if (row?.unchanged) return;
        if (row?.revision) revisionRef.current = row.revision;
        registerPicks(row);
        setPayload(row);
        setError("");
      } catch {
        if (!cancelled) {
          setError("Could not load live draft data.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    const pollId = window.setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
    };
  }, []);

  const sessionTitle = payload?.title || `${LIVE_DRAFT_YEAR} NHL Draft — Live`;
  const progress =
    payload && payload.total_picks
      ? Math.round((payload.picks_made / payload.total_picks) * 100)
      : 0;

  return (
    <>
      <Helmet>
        <title>{sessionTitle} - Hockey Pipeline</title>
        <meta
          name="description"
          content={`Live ${LIVE_DRAFT_YEAR} NHL Draft tracker with picks, on-the-clock team, and remaining prospect odds.`}
        />
        <link rel="canonical" href="https://hockeypipeline.com/live-draft" />
      </Helmet>
      <PageLoader loading={loading && !payload} />
      {celebrationPick && (
        <PickRevealOverlay
          pick={celebrationPick}
          onDismiss={() => setCelebrationPick(null)}
        />
      )}
      <Container className="container top-container live-draft-page" maxWidth={false}>
        <header className="live-draft-header">
          <div className="live-draft-header-main">
            <div className="live-draft-header-copy">
              <p className="live-draft-eyebrow">Live draft</p>
              <Typography variant="h4" component="h1" className="live-draft-page-title">
                {sessionTitle}
              </Typography>
            </div>
            {payload && (
              <div className="live-draft-header-progress">
                <span className="live-draft-progress-label">
                  {payload.picks_made} / {payload.total_picks} picks
                </span>
                <div className="live-draft-progress-bar" aria-hidden="true">
                  <div
                    className="live-draft-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </header>

        {error && <p className="live-draft-error">{error}</p>}

        {!loading && !payload && !error && (
          <section className="live-draft-panel live-draft-panel--empty">
            <h2 className="live-draft-panel-title">No draft data</h2>
            <p className="live-draft-muted">
              Could not load the {LIVE_DRAFT_YEAR} live draft feed.
            </p>
          </section>
        )}

        {payload && (
          <div className="live-draft-board-layout">
            <aside className="live-draft-col live-draft-col--left">
              <RemainingBoard players={payload.available_players} />
            </aside>

            <main className="live-draft-col live-draft-col--center">
              <ClockCenter slot={payload.on_the_clock} upcoming={payload.upcoming_picks} />
            </main>

            <aside className="live-draft-col live-draft-col--right">
              <PicksFeed
                picks={payload.previous_picks}
                sortMode={feedSort}
                onSortModeChange={setFeedSort}
                highlightPickKey={highlightPickKey}
              />
            </aside>
          </div>
        )}
      </Container>
    </>
  );
}

export default LiveDraft;
